import { useTranslation } from 'react-i18next'
import { useAppStore } from '~/hooks'
import { type BasemapType } from '~/constants'
import { cn } from '~/util'
import { Text } from '~/components/typography'
import basemapFlatImage from '~/assets/basemap_flat.png'
import basemapSatelliteImage from '~/assets/basemap_satellite.png'
import './BasemapSelector.css'
import { track } from '~/analytics'

const basemapOptions = [
  {
    type: 'FLAT' as BasemapType,
    nameKey: 'basemap.base',
    image: basemapFlatImage,
  },
  {
    type: 'SATELLITE' as BasemapType,
    nameKey: 'basemap.satellite',
    image: basemapSatelliteImage,
  },
] as const

function BasemapSelector() {
  const { t } = useTranslation()
  const { basemapType, setBasemapType } = useAppStore()

  const isFlat = basemapType === 'FLAT'
  const borderColor = isFlat ? 'border-neutral-midfi-grey' : 'border-neutral-off-white'
  const hoverBorderColor = isFlat
    ? 'hover:border-neutral-midfi-grey'
    : 'hover:border-neutral-off-white'

  const handleBasemapChange = (nextBasemap: BasemapType) => {
    setBasemapType(nextBasemap)
    track('basemap_selected', { basemap: nextBasemap })
  }

  return (
    <div className="flex justify-end gap-2 pt-4">
      {basemapOptions.map((option) => {
        const isSelected = basemapType === option.type

        return (
          <button
            key={option.type}
            onClick={() => handleBasemapChange(option.type)}
            className="flex flex-col items-center gap-1"
          >
            <div
              className={cn(
                'overflow-hidden rounded-lg border-[1px]',
                hoverBorderColor,
                isSelected ? `border-[3px] ${borderColor}` : `m-[2px] border-transparent`
              )}
            >
              <img src={option.image} alt={t(option.nameKey)} className="h-10 w-10 object-cover" />
            </div>
            <Text
              level={4}
              className={cn(
                'text-xs',
                isSelected ? 'font-bold' : 'font-normal',
                isFlat
                  ? 'basemap-selector-text-halo text-neutral-midfi-grey'
                  : 'text-neutral-off-white'
              )}
            >
              {t(option.nameKey)}
            </Text>
          </button>
        )
      })}
    </div>
  )
}

export default BasemapSelector
